import { useAuth, useSignIn, useUser } from "@clerk/clerk-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import Loading from "components/Loading";
import { trackEvent } from "utils/tracking";

const AcceptToken = () => {
  const { isLoaded: authLoaded, isSignedIn, signOut } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();

  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const redirectUrl = searchParams.get("redirect");
  const signInToken = searchParams.get("token");
  const targetEmail = searchParams.get("email");

  const isSameUser = useMemo(() => {
    if (!authLoaded || !userLoaded) return false;
    if (!isSignedIn || !targetEmail) return false;
    return user?.primaryEmailAddress?.emailAddress === targetEmail;
  }, [authLoaded, userLoaded, isSignedIn, targetEmail, user]);

  const attemptedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!signInToken || !redirectUrl) return;

    if (!authLoaded || !userLoaded || !signInLoaded) return;

    if (isSameUser) {
      setLoading(false);
      attemptedTokenRef.current = signInToken;
      return;
    }

    if (attemptedTokenRef.current === signInToken) {
      setLoading(false);
      return;
    }
    attemptedTokenRef.current = signInToken;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        if (isSignedIn) {
          await signOut();
        }

        const attempt = await signIn!.create({
          strategy: "ticket",
          ticket: signInToken,
        });

        if (attempt.status === "complete") {
          await setActive({ session: attempt.createdSessionId });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    authLoaded,
    userLoaded,
    signInLoaded,
    isSameUser,
    isSignedIn,
    signOut,
    signIn,
    setActive,
    signInToken,
    redirectUrl,
  ]);

  useEffect(() => {
    trackEvent("Mobile App Sign-in Link Pressed", {
      "Redirect Uri": redirectUrl,
      targetEmail,
    });
  }, [redirectUrl, targetEmail]);

  if (!signInToken || !redirectUrl) {
    return <Navigate to="/sign-in" replace />;
  }

  if (loading && !isSignedIn) {
    return <Loading />;
  }

  return <Navigate to={redirectUrl} replace />;
};

export default AcceptToken;
